import React, { useState, useRef, useEffect, useCallback } from 'react';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import './ContactUs.css';

function ContactUs() {
  // API config from environment variables
  const CONFIG = {
    apiEndpoint: process.env.REACT_APP_CONTACT_ENDPOINT,
    hCaptchaSiteKey: process.env.REACT_APP_HCAPTCHA_SITE_KEY
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  // References
  const formRef = useRef(null);
  const submitButtonRef = useRef(null);
  const buttonTextRef = useRef(null);
  const buttonLoaderRef = useRef(null);
  const captchaRef = useRef(null);
  const captchaWidgetId = useRef(null);

  // Utility functions
  const encryptData = (data) => {
    return btoa(JSON.stringify(data));
  };

  const generateContactID = () => {
    const randomNumbers = Math.floor(Math.random() * 100000000).toString().padStart(8, '0');
    return 'CONTACT' + randomNumbers;
  };

  // hCaptcha callbacks
  const onCaptchaVerify = useCallback((token) => {
    console.log('hCaptcha verified');
    setCaptchaToken(token);
  }, []);

  const onCaptchaExpire = useCallback(() => {
    console.log('hCaptcha expired');
    setCaptchaToken('');
  }, []);

  const onCaptchaError = useCallback((err) => {
    console.error('hCaptcha error:', err);
    setCaptchaToken('');
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Set loading state
  const setLoading = (isLoading) => {
    if (submitButtonRef.current) {
      submitButtonRef.current.disabled = isLoading;
    }
    if (buttonTextRef.current) {
      buttonTextRef.current.style.display = isLoading ? 'none' : 'block';
    }
    if (buttonLoaderRef.current) {
      buttonLoaderRef.current.style.display = isLoading ? 'block' : 'none';
    }
    setIsSubmitting(isLoading);
  };

  // Show error message
  const showError = (message) => {
    setFormError(message);
    setTimeout(() => {
      setFormError('');
    }, 5000);
  };

  // Show success message and redirect
  const showSuccess = () => {
    setFormSuccess(true);
    setFormData({ name: '', email: '', message: '' });

    // Reset hCaptcha
    if (window.hcaptcha && captchaWidgetId.current !== null) {
      window.hcaptcha.reset(captchaWidgetId.current);
    }
    setCaptchaToken('');

    // Redirect to thank you page
    setTimeout(() => {
      window.location.href = '/thank-you';
    }, 1500);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission started');

    // Basic validation
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.message) newErrors.message = 'Message is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Validate hCaptcha
    if (!captchaToken) {
      showError('Please complete the captcha verification');
      return;
    }

    setLoading(true);

    try {
      // Prepare encrypted form data
      const rawFormData = {
        [generateContactID()]: {
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }
      };

      // Send encrypted data
      const encryptedFormData = {
        data: encryptData(rawFormData)
      };

      console.log('Sending request to:', CONFIG.apiEndpoint);

      const response = await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Captcha-Token': captchaToken
        },
        body: JSON.stringify(encryptedFormData)
      });

      const result = await response.json();
      console.log('API Response received (status:', response.status, ')');

      if (!response.ok) {
        throw new Error(result.message || 'Form submission failed');
      }

      showSuccess();
    } catch (error) {
      console.error('Submission error:', error);
      showError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
      // Reset captcha after submission attempt
      if (window.hcaptcha && captchaWidgetId.current !== null) {
        window.hcaptcha.reset(captchaWidgetId.current);
      }
      setCaptchaToken('');
    }
  };

  // Load and render hCaptcha
  useEffect(() => {
    // Define callback functions on window for hCaptcha
    window.onContactCaptchaVerify = onCaptchaVerify;
    window.onContactCaptchaExpire = onCaptchaExpire;
    window.onContactCaptchaError = onCaptchaError;

    const renderCaptcha = () => {
      if (window.hcaptcha && captchaRef.current && captchaWidgetId.current === null) {
        try {
          captchaWidgetId.current = window.hcaptcha.render(captchaRef.current, {
            sitekey: CONFIG.hCaptchaSiteKey,
            callback: 'onContactCaptchaVerify',
            'expired-callback': 'onContactCaptchaExpire',
            'error-callback': 'onContactCaptchaError',
          });
          console.log('hCaptcha rendered successfully');
        } catch (err) {
          console.error('Error rendering hCaptcha:', err);
        }
      }
    };

    // Check if hCaptcha script is already loaded
    if (window.hcaptcha) {
      renderCaptcha();
    } else {
      // Load the script
      const script = document.createElement('script');
      script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Small delay to ensure hcaptcha is ready
        setTimeout(renderCaptcha, 100);
      };
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup
      delete window.onContactCaptchaVerify;
      delete window.onContactCaptchaExpire;
      delete window.onContactCaptchaError;
    };
  }, [CONFIG.hCaptchaSiteKey, onCaptchaVerify, onCaptchaExpire, onCaptchaError]);

  return (
    <div className="contact-us-page">
      <Header />
      <main className="contact-section">
        <section className="contact-container">
          <h1>Contact Us</h1>
          <p>Send us a message and we'll help out as soon as we can.</p>

          <form id="contact-form" className="form-container" onSubmit={handleSubmit} ref={formRef}>
            <div className="form-group">
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Name"
                required
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <div id="nameError" className="error-message" style={{display: 'block'}}>{errors.name}</div>}
            </div>

            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email"
                required
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <div id="emailError" className="error-message" style={{display: 'block'}}>{errors.email}</div>}
            </div>

            <div className="form-group">
              <textarea
                id="message"
                name="message"
                placeholder="Message"
                required
                value={formData.message}
                onChange={handleChange}
              ></textarea>
              {errors.message && <div id="messageError" className="error-message" style={{display: 'block'}}>{errors.message}</div>}
            </div>

            {/* hCaptcha container - explicit render */}
            <div className="captcha-container">
              <div ref={captchaRef}></div>
            </div>

            {formError && <div id="formErrorMessage" className="error-message" style={{display: 'block'}}>{formError}</div>}
            {formSuccess && <div id="formSuccessMessage" className="success-message" style={{display: 'block'}}>Message sent successfully!</div>}

            <button type="submit" id="submitButton" className="submit-button" ref={submitButtonRef} disabled={isSubmitting}>
              <span className="button-text" ref={buttonTextRef}>Send Message</span>
              <span className="button-loader" ref={buttonLoaderRef} style={{display: 'none'}}>Sending...</span>
            </button>
          </form>
        </section>
      </main>

      <section className="outro-section">
        <h2>Leasing for the best price <br /> should not be hard!</h2>
        <p>Let us help you obtain the best price</p>
        <div className="support-buttons">
          <a href="https://www.buymeacoffee.com/LeasEizi" target="_blank" rel="noopener noreferrer" className="support-btn">Buy Us A Pizza 🍕</a>
          <a href="https://pay.leaseizi.com/" target="_blank" rel="noopener noreferrer" className="support-btn">Support LeasEiZi</a>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default ContactUs;
